import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  inputStateForValue,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  isWithinBounds,
  queryIsLoading,
  useCropMethodology,
  useGetFootprintingCropPrimaryDataById,
  useMutateFootprintingCropPrimaryData,
  usePracticeManagementCropParameter,
  useTranslation,
} from "utils";

import { HARD_INPUT_STATES, HARVEST_UNIT_VALUE, HarvestUnitValue, toTonnesPerHa } from "consts";

import { CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE, ENUM_CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, InputNumberWithUnitSelector, Option, PageSpin, SanityCheck, Select } from "components";

type CropGeneralProps = {
  readOnly?: boolean;
  primaryDataId?: string;
  farmId: string;
  cropId: string;
  year: number;
};

const PRIMARY_DATA_FIELDS = [
  "totalWeightPerHa",
  "totalWeightPerHaUnit",
  "preDryingMoistureContentPct",
  "byproductPct",
  "seedWeightKgPerHa",
  "soilPh",
  "residueManagementPractice",
] as const;

const hasAnySavedValue = (primaryData?: Partial<Record<(typeof PRIMARY_DATA_FIELDS)[number], unknown>>) =>
  PRIMARY_DATA_FIELDS.some((field) => !isNullOrUndefined(primaryData?.[field]));

const CropGeneralForm = ({ readOnly, primaryDataId, farmId, cropId, year }: CropGeneralProps) => {
  const { t } = useTranslation("footprintingCrop");
  const formRef = useRef<FormInstance>(null);
  const { isCfp, isKlim } = useCropMethodology(farmId);

  const [totalWeightPerHa, setTotalWeightPerHa] = useState<number | null>(null);
  const [totalWeightPerHaUnit, setTotalWeightPerHaUnit] = useState<HarvestUnitValue | null>(null);
  const [preDryingMoistureContentPct, setPreDryingMoistureContentPct] = useState<number | null>(null);
  const [byproductPct, setByproductPct] = useState<number | null>(null);
  const [seedWeightKgPerHa, setSeedWeightKgPerHa] = useState<number | null>(null);
  const [soilPh, setSoilPh] = useState<number | null>(null);
  const [residueManagementPractice, setResidueManagementPractice] = useState<ENUM_CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE | null>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");
  const cropParametersQuery = usePracticeManagementCropParameter(cropId);

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      cropId,
      farmId,
      year,
      totalWeightPerHa,
      totalWeightPerHaUnit,
      preDryingMoistureContentPct,
      byproductPct,
      seedWeightKgPerHa,
      soilPh,
      residueManagementPractice,
    },
    primaryDataId
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setTotalWeightPerHa(cropPrimaryDataQuery.data.totalWeightPerHa ?? null);
      setTotalWeightPerHaUnit(cropPrimaryDataQuery.data.totalWeightPerHaUnit ?? null);
      setPreDryingMoistureContentPct(cropPrimaryDataQuery.data.preDryingMoistureContentPct ?? null);
      setByproductPct(cropPrimaryDataQuery.data.byproductPct ?? null);
      setSeedWeightKgPerHa(cropPrimaryDataQuery.data.seedWeightKgPerHa ?? null);
      setSoilPh(cropPrimaryDataQuery.data.soilPh ?? null);
      setResidueManagementPractice(cropPrimaryDataQuery.data.residueManagementPractice ?? null);

      formRef.current?.setFieldsValue({
        totalWeight: cropPrimaryDataQuery.data.totalWeightPerHa ?? null,
        totalWeightUnit: cropPrimaryDataQuery.data.totalWeightPerHaUnit ?? null,
        preDryingMoistureContentPct: formatFractionInput(cropPrimaryDataQuery.data.preDryingMoistureContentPct ?? null),
        byproductPct: formatFractionInput(cropPrimaryDataQuery.data.byproductPct ?? null),
        seedWeightKgPerHa: cropPrimaryDataQuery.data.seedWeightKgPerHa ?? null,
        soilPh: cropPrimaryDataQuery.data.soilPh ?? null,
        residueManagementPractice: cropPrimaryDataQuery.data.residueManagementPractice ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      if (hasAnySavedValue(cropPrimaryDataQuery.data) && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  const isLoading = queryIsLoading(cropPrimaryDataQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  const totalWeightInTs = toTonnesPerHa(totalWeightPerHa, totalWeightPerHaUnit);
  const isTotalWeightInvalid =
    hasAnySavedValue(cropPrimaryDataQuery.data) &&
    (totalWeightInTs === null ||
      totalWeightPerHaUnit === null ||
      HARD_INPUT_STATES.includes(inputStateForValue(totalWeightInTs, cropParametersQuery.data?.harvestTonsPerHectare)));

  return (
    <Form ref={formRef}>
      <Card border dataTestId="footprinting-crop-general-form" className="padding-200">
        <Flex vertical gap={16}>
          <InputNumberWithUnitSelector
            disabled={readOnly}
            name="totalWeight"
            className="hide-error-message"
            dataTestId="footprinting-crop-yield-weight-input"
            label={t("cropGeneralForm.question.totalWeight")}
            tooltip={t("cropGeneralForm.tooltips.totalWeight")}
            value={totalWeightPerHa}
            units={[HARVEST_UNIT_VALUE.tonnesPerHa, HARVEST_UNIT_VALUE.decitonnesPerHa]}
            initialValue={totalWeightPerHa}
            unitValue={totalWeightPerHaUnit ?? HARVEST_UNIT_VALUE.tonnesPerHa}
            onChange={(v, u) => {
              setTotalWeightPerHa(v);
              setTotalWeightPerHaUnit(u as HarvestUnitValue | null);
            }}
            guidanceText={cropParametersQuery.data?.harvestTonsPerHectare}
            validateStatus={isTotalWeightInvalid ? "error" : undefined}
          />
          {totalWeightInTs ? <SanityCheck inputValue={totalWeightInTs} inputRange={cropParametersQuery.data?.harvestTonsPerHectare} /> : null}

          <InputNumber
            disabled={readOnly}
            name="preDryingMoistureContentPct"
            dataTestId="footprinting-crop-moisture-content-input"
            label={t("cropGeneralForm.question.preDryingMoistureContentPct")}
            tooltip={t("cropGeneralForm.tooltips.preDryingMoistureContentPct")}
            suffix="%"
            value={formatFractionInput(preDryingMoistureContentPct)}
            initialValue={formatFractionInput(preDryingMoistureContentPct)}
            onChange={(v) => setPreDryingMoistureContentPct(formatPercentageInput(v))}
            precision={2}
            validateFirst
            rules={[
              { required: true, message: t("cropGeneralForm.error.preDryingMoistureContentPct") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                    return Promise.reject(new Error(t("cropGeneralForm.error.preDryingMoistureContentPct")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />

          {isKlim && (
            <InputNumber
              disabled={readOnly}
              name="byproductPct"
              dataTestId="footprinting-crop-byproduct-input"
              label={t("cropGeneralForm.question.byproductPct")}
              tooltip={t("cropGeneralForm.tooltips.byproductPct")}
              suffix="%"
              value={formatFractionInput(byproductPct)}
              initialValue={formatFractionInput(byproductPct)}
              onChange={(v) => setByproductPct(formatPercentageInput(v))}
              precision={2}
              validateFirst
              rules={[
                { required: true, message: t("cropGeneralForm.error.byproductPct") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                      return Promise.reject(new Error(t("cropGeneralForm.error.byproductPct")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          )}

          {isCfp && (
            <Select
              disabled={readOnly}
              name="residueManagementPractice"
              dataTestId="footprinting-crop-residue-management-input"
              label={t("cropGeneralForm.question.residueManagementPractice")}
              tooltip={t("cropGeneralForm.tooltips.residueManagementPractice")}
              value={residueManagementPractice}
              initialValue={residueManagementPractice}
              onChange={(v: ENUM_CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE) => setResidueManagementPractice(v)}
            >
              {Object.values(CFP_CROP_RESIDUE_MANAGEMENT_PRACTICE).map((practice) => (
                <Option key={practice} value={practice}>
                  {t(`cropGeneralForm.residueManagementPractice.${practice}`)}
                </Option>
              ))}
            </Select>
          )}

          <InputNumber
            disabled={readOnly}
            name="seedWeightKgPerHa"
            dataTestId="footprinting-crop-seeds-weight-input"
            label={t("cropGeneralForm.question.seedWeightKgPerHa")}
            tooltip={t("cropGeneralForm.tooltips.seedWeightKgPerHa")}
            suffix="kg/ha"
            value={seedWeightKgPerHa}
            initialValue={seedWeightKgPerHa}
            onChange={(v) => setSeedWeightKgPerHa(v)}
            validateFirst
            rules={[
              { required: true, message: t("cropGeneralForm.error.seedWeightKgPerHa") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                    return Promise.reject(new Error(t("cropGeneralForm.error.seedWeightKgPerHa")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />

          <InputNumber
            disabled={readOnly}
            name="soilPh"
            dataTestId="footprinting-crop-soil-ph-input"
            label={t("cropGeneralForm.question.soilPh")}
            tooltip={t("cropGeneralForm.tooltips.soilPh")}
            suffix="pH"
            value={soilPh}
            initialValue={soilPh}
            onChange={(v) => setSoilPh(v)}
            validateFirst
            rules={[
              { required: true, message: t("cropGeneralForm.error.soilPh") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isWithinBounds(v, 3, 9)) {
                    return Promise.reject(new Error(t("cropGeneralForm.error.soilPh")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
        </Flex>
      </Card>
    </Form>
  );
};

CropGeneralForm.Key = "crop.general";
export { CropGeneralForm };
